/**
 * Utility functions for deal finder components
 */

import { useEntitlementKey } from "hooks";
import { useCopyValue } from "hooks/useCopies";
import type { DealFinderTicker } from "api/tickers/models/DealFinderTicker";
import { useSelector } from "react-redux";
import type { RootState } from "state/rootReducer";

/**
 * Returns the appropriate color for an investment rating
 * @param rating - The investment rating (number)
 * @returns The color string for the rating
 */
export const getInvestmentRatingColor = (rating: number): string => {
  if (rating <= 5) return "gray.200";
  if (rating <= 6) return "orange.200";
  if (rating <= 7) return "purple.200";
  if (rating <= 8) return "blue.200";
  if (rating <= 9) return "teal.200";
  return "green.200";
};

/**
 * Determines if a deal is allowed based on private restricted list settings
 * @param showPrivateRestrictedList - Whether to show private restricted list
 * @param privateRestrictedList - Array of restricted symbols
 * @param dealSymbol - The deal symbol to check
 * @returns Whether the deal is allowed
 */
export const useCheckDealRestricted = (dealSymbol: string): boolean => {
  const showPrivateRestrictedList = useEntitlementKey("ui_show_private_tickers_restricted");
  const privateRestrictedList = useCopyValue("copy_ticker_private_restricted_list") as string[];
  if (!showPrivateRestrictedList) return false;
  return !privateRestrictedList.includes(dealSymbol);
};

export const useCheckUserHasCredit = (creditCost?: number | null): boolean => {
  const usageData = useSelector((state: RootState) => state.usage?.featureUsage || {});
  const pendingDeductions = useSelector((state: RootState) => state.pendingCredits?.totalPendingDeductions || 0);

  const projectRunsData = usageData.project_runs;
  const used = projectRunsData && "used" in projectRunsData ? projectRunsData.used : 0;
  const allocated = projectRunsData && "allocated" in projectRunsData ? projectRunsData.allocated : 0;
  const remainingCredits = Math.max(0, allocated - used - pendingDeductions);

  if (creditCost && creditCost > 0) {
    if (remainingCredits < creditCost) {
      return false;
    }
  }

  return true;
};

export const useCheckUserCurrentCredit = () => {
  const usageData = useSelector((state: RootState) => state.usage?.featureUsage || {});
  const pendingDeductions = useSelector((state: RootState) => state.pendingCredits?.totalPendingDeductions || 0);

  const projectRunsData = usageData.project_runs;
  const used = projectRunsData && "used" in projectRunsData ? projectRunsData.used : 0;
  const allocated = projectRunsData && "allocated" in projectRunsData ? projectRunsData.allocated : 0;
  const remainingCredits = Math.max(0, allocated - used - pendingDeductions);

  return {
    used,
    allocated,
    remaining: remainingCredits,
    pendingDeductions,
  };
};

/**
 * Determines if a deal tier is allowed based on user's plan
 * @param pricingStatus - The pricing tier of the deal (e.g., "tier_0", "tier_1", etc.)
 * @returns Whether the deal tier is allowed for the current user
 */
export const useCheckDealStatusAllowed = (pricingStatus?: string | null): boolean => {
  if (!pricingStatus || pricingStatus === "pending") {
    return false;
  }
  return true;
};

/**
 * Determines if a deal is free tier (tier_0)
 * @param deal - The deal object to check
 * @returns Whether the deal is free tier
 */
export const isDealTierFree = (deal?: DealFinderTicker | null): boolean => {
  return deal?.pricingTier === "tier_0";
};
